import { getCurrentLanguage } from "@abp";
import {
  CalendarOutlined,
  ClockCircleOutlined,
  CloseOutlined,
  GlobalOutlined,
  HeartOutlined,
  HistoryOutlined,
  LockOutlined,
  LogoutOutlined,
  MailOutlined,
  NotificationOutlined,
  QuestionCircleOutlined,
  RightOutlined,
  UserOutlined,
} from "@ant-design/icons";
import "flag-icons/css/flag-icons.min.css";
import { useLatestAnnouncements, useNextBooking, useUser } from "@hooks";
import { salonTime, SharedPopconfirm } from "@shared";
import { useStore } from "@stores";
import { Drawer, Skeleton } from "antd";
import { observer } from "mobx-react";
import React from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import Styles from "./TopHeaderMenu.styles";

interface IProps {
  open: boolean;
  onClose: () => void;
}

/**
 * USER DRAWER — Rich slide-in menu for logged-in users.
 *
 * Sections: profile header (gradient), upcoming booking, shortcuts grid,
 * account, support, language toggle, footer (close + logout).
 *
 * Opened by the avatar in TopHeaderMenu; closed via the footer button,
 * the overlay, or after any navigation tile is clicked.
 */
export const UserDrawer: React.FC<IProps> = observer(({ open, onClose }) => {
  const { t } = useTranslation();
  const { authStore } = useStore();
  const navigate = useNavigate();

  const { nextBooking, loading: nextBookingLoading } = useNextBooking(open);
  const profileUser = useUser(authStore.currentUserId?.toString());
  const { count } = useLatestAnnouncements();

  const isSr = getCurrentLanguage().startsWith("sr");

  const fullName = profileUser
    ? `${profileUser.name ?? ""} ${profileUser.surname ?? ""}`.trim()
    : "";
  const initials = profileUser
    ? `${profileUser.name?.[0] ?? ""}${profileUser.surname?.[0] ?? ""}`.toUpperCase()
    : "";

  /** Navigate to a route and close the drawer in one click handler. */
  const goTo = (path: string) => () => {
    navigate(path);
    onClose();
  };

  const formatBookingWhen = (start: string | undefined, timeZone?: string): string => {
    if (!start) return "";
    // "Today"/"Tomorrow" mean days at the salon, so count them there
    const d = salonTime(start, timeZone);
    const diffDays = d.startOf("day").diff(salonTime(undefined, timeZone).startOf("day"), "day");
    if (diffDays === 0) return t("Today");
    if (diffDays === 1) return t("Tomorrow");
    return d.locale(isSr ? "sr" : "en").format("DD. MMM");
  };

  return (
    <Drawer
      title={
        authStore.currentUserId ? (
          <Styles.DrawerHeaderProfile onClick={goTo("/my-profile/edit")}>
            <Styles.DrawerHeaderAvatar>
              {initials || <UserOutlined />}
            </Styles.DrawerHeaderAvatar>
            <Styles.DrawerHeaderInfo>
              <Styles.DrawerHeaderName>
                {fullName || t("MyProfile")}
              </Styles.DrawerHeaderName>
              {profileUser?.emailAddress && (
                <Styles.DrawerHeaderEmail>
                  {profileUser.emailAddress}
                </Styles.DrawerHeaderEmail>
              )}
            </Styles.DrawerHeaderInfo>
          </Styles.DrawerHeaderProfile>
        ) : (
          <Styles.DrawerTitle>{t("MenuTitle")}</Styles.DrawerTitle>
        )
      }
      closable={false}
      placement="right"
      onClose={onClose}
      open={open}
      footer={
        authStore.currentUserId ? (
          <Styles.DrawerBottomActions>
            <Styles.DrawerCloseAction onClick={onClose}>
              <CloseOutlined />
              {t("CloseButton")}
            </Styles.DrawerCloseAction>
            <SharedPopconfirm
              onConfirm={() => {
                authStore.logout();
                onClose();
              }}
            >
              <Styles.DrawerLogoutAction>
                <LogoutOutlined />
                {t("LogOutMenu")}
              </Styles.DrawerLogoutAction>
            </SharedPopconfirm>
          </Styles.DrawerBottomActions>
        ) : null
      }
      styles={{
        body: {
          padding: 0,
          background: "var(--color-bg-subtle)",
          display: "flex",
          flexDirection: "column",
          paddingTop: "var(--space-4)",
        },
        header: authStore.currentUserId
          ? {
              background: "var(--color-gradient)",
              border: "none",
              boxShadow: "var(--shadow-primary)",
            }
          : {
              background: "var(--color-bg-subtle)",
              borderBottom: "1px solid var(--color-border-accent)",
            },
        footer: {
          background: "var(--color-bg-subtle)",
          borderTop: "1px solid var(--color-border-accent)",
          padding: "var(--space-3) 0",
        },
      }}
    >
      {/* Upcoming booking — soonest future appointment, or empty state */}
      {authStore.currentUserId && (
        <>
          {nextBookingLoading && !nextBooking ? (
            <Styles.UpcomingSkeleton>
              <Skeleton active paragraph={{ rows: 2 }} title={false} />
            </Styles.UpcomingSkeleton>
          ) : nextBooking ? (
            <Styles.UpcomingBookingCard onClick={goTo("/my-bookings")}>
              <Styles.UpcomingBookingLabel>
                <span>{t("UpcomingBooking")}</span>
                <Styles.UpcomingBookingWhen>
                  <CalendarOutlined style={{ fontSize: 11 }} />
                  {formatBookingWhen(nextBooking.start, nextBooking.tenant?.timeZone)}
                </Styles.UpcomingBookingWhen>
              </Styles.UpcomingBookingLabel>
              <Styles.UpcomingBookingTitle>
                {nextBooking.title}
              </Styles.UpcomingBookingTitle>
              <Styles.UpcomingBookingTenant>
                {nextBooking.tenant?.name}
              </Styles.UpcomingBookingTenant>
              {nextBooking.start && (
                <Styles.UpcomingBookingTime>
                  <ClockCircleOutlined />
                  {salonTime(nextBooking.start, nextBooking.tenant?.timeZone).format("HH:mm")}
                  {nextBooking.end && (
                    <>
                      <span
                        style={{
                          color: "var(--color-text-tertiary)",
                          fontWeight: 400,
                        }}
                      >
                        →
                      </span>
                      <span
                        style={{
                          color: "var(--color-text-secondary)",
                          fontWeight: 500,
                        }}
                      >
                        {salonTime(nextBooking.end, nextBooking.tenant?.timeZone).format("HH:mm")}
                      </span>
                    </>
                  )}
                </Styles.UpcomingBookingTime>
              )}
            </Styles.UpcomingBookingCard>
          ) : (
            <Styles.UpcomingEmptyCard>
              <Styles.UpcomingEmptyIcon>
                <CalendarOutlined />
              </Styles.UpcomingEmptyIcon>
              <Styles.UpcomingEmptyText>
                <Styles.UpcomingEmptyTitle>
                  {t("NoUpcomingBookings")}
                </Styles.UpcomingEmptyTitle>
                <Styles.UpcomingEmptyHint>
                  {t("NoUpcomingBookingsHint")}
                </Styles.UpcomingEmptyHint>
              </Styles.UpcomingEmptyText>
              <Styles.UpcomingEmptyCta onClick={goTo("/")}>
                {t("FindSalon")}
              </Styles.UpcomingEmptyCta>
            </Styles.UpcomingEmptyCard>
          )}
        </>
      )}

      {/* Quick shortcuts — jumps to other pages (Favorites, History, Announcements) */}
      {authStore.currentUserId && (
        <>
          <Styles.DrawerSectionLabel>
            {t("Shortcuts")}
          </Styles.DrawerSectionLabel>
          <Styles.ShortcutsGrid>
            <Styles.ShortcutTile onClick={goTo("/favorites")}>
              <Styles.ShortcutIcon $tint="rgba(239, 68, 68, 0.12)">
                <HeartOutlined />
              </Styles.ShortcutIcon>
              {t("FavoriteTenants")}
            </Styles.ShortcutTile>

            <Styles.ShortcutTile onClick={goTo("/my-bookings?tab=Past")}>
              <Styles.ShortcutIcon>
                <HistoryOutlined />
              </Styles.ShortcutIcon>
              {t("TabPast")}
            </Styles.ShortcutTile>

            <Styles.ShortcutTile onClick={goTo("/announcements")}>
              <Styles.ShortcutIconWrap>
                <Styles.ShortcutIcon>
                  <NotificationOutlined />
                </Styles.ShortcutIcon>
                {count > 0 && (
                  <Styles.ShortcutBadge>
                    {count > 99 ? "99+" : count}
                  </Styles.ShortcutBadge>
                )}
              </Styles.ShortcutIconWrap>
              {t("Announcements")}
            </Styles.ShortcutTile>
          </Styles.ShortcutsGrid>
        </>
      )}

      {/* Account — iOS-style menu cards with icon square + chevron */}
      {authStore.currentUserId && (
        <>
          <Styles.DrawerSectionLabel>{t("Account")}</Styles.DrawerSectionLabel>
          <Styles.DrawerAccountItem onClick={goTo("/my-profile/edit")}>
            <Styles.DrawerAccountItemLeft>
              <Styles.DrawerAccountItemIcon>
                <UserOutlined />
              </Styles.DrawerAccountItemIcon>
              <Styles.DrawerAccountItemLabel>
                {t("MyProfile")}
              </Styles.DrawerAccountItemLabel>
            </Styles.DrawerAccountItemLeft>
            <Styles.DrawerAccountItemChevron>
              <RightOutlined />
            </Styles.DrawerAccountItemChevron>
          </Styles.DrawerAccountItem>

          <Styles.DrawerAccountItem onClick={goTo("/password")}>
            <Styles.DrawerAccountItemLeft>
              <Styles.DrawerAccountItemIcon>
                <LockOutlined />
              </Styles.DrawerAccountItemIcon>
              <Styles.DrawerAccountItemLabel>
                {t("ChangePasswordTitle")}
              </Styles.DrawerAccountItemLabel>
            </Styles.DrawerAccountItemLeft>
            <Styles.DrawerAccountItemChevron>
              <RightOutlined />
            </Styles.DrawerAccountItemChevron>
          </Styles.DrawerAccountItem>

          <Styles.DrawerAccountItem onClick={goTo("/my-bookings")}>
            <Styles.DrawerAccountItemLeft>
              <Styles.DrawerAccountItemIcon>
                <CalendarOutlined />
              </Styles.DrawerAccountItemIcon>
              <Styles.DrawerAccountItemLabel>
                {t("MyBookingsMenu")}
              </Styles.DrawerAccountItemLabel>
            </Styles.DrawerAccountItemLeft>
            <Styles.DrawerAccountItemChevron>
              <RightOutlined />
            </Styles.DrawerAccountItemChevron>
          </Styles.DrawerAccountItem>
        </>
      )}

      {/* Support — help and contact (same iOS-style menu cards) */}
      {authStore.currentUserId && (
        <>
          <Styles.DrawerSectionLabel>{t("Support")}</Styles.DrawerSectionLabel>
          <Styles.DrawerAccountItem onClick={goTo("/kako-funkcionise")}>
            <Styles.DrawerAccountItemLeft>
              <Styles.DrawerAccountItemIcon>
                <QuestionCircleOutlined />
              </Styles.DrawerAccountItemIcon>
              <Styles.DrawerAccountItemLabel>
                {t("HelpMenu")}
              </Styles.DrawerAccountItemLabel>
            </Styles.DrawerAccountItemLeft>
            <Styles.DrawerAccountItemChevron>
              <RightOutlined />
            </Styles.DrawerAccountItemChevron>
          </Styles.DrawerAccountItem>

          <Styles.DrawerAccountItem onClick={goTo("/contact")}>
            <Styles.DrawerAccountItemLeft>
              <Styles.DrawerAccountItemIcon>
                <MailOutlined />
              </Styles.DrawerAccountItemIcon>
              <Styles.DrawerAccountItemLabel>
                {t("ContactMenu")}
              </Styles.DrawerAccountItemLabel>
            </Styles.DrawerAccountItemLeft>
            <Styles.DrawerAccountItemChevron>
              <RightOutlined />
            </Styles.DrawerAccountItemChevron>
          </Styles.DrawerAccountItem>
        </>
      )}

      {/* Settings — language toggle */}
      {authStore.currentUserId && (
        <>
          <Styles.DrawerSectionLabel>{t("Settings")}</Styles.DrawerSectionLabel>
          <Styles.DrawerSettingsRow>
            <Styles.DrawerAccountItemLeft>
              <Styles.DrawerAccountItemIcon>
                <GlobalOutlined />
              </Styles.DrawerAccountItemIcon>
              <Styles.DrawerAccountItemLabel>
                {t("Language")}
              </Styles.DrawerAccountItemLabel>
            </Styles.DrawerAccountItemLeft>
            <Styles.DrawerLangSwitch>
              <Styles.LangOption
                $active={isSr}
                onClick={() => !isSr && authStore.changeLanguage("sr")}
              >
                <span className="fi fi-rs" />
                SR
              </Styles.LangOption>
              <Styles.LangOption
                $active={!isSr}
                onClick={() => isSr && authStore.changeLanguage("en")}
              >
                <span className="fi fi-gb" />
                EN
              </Styles.LangOption>
            </Styles.DrawerLangSwitch>
          </Styles.DrawerSettingsRow>
        </>
      )}
    </Drawer>
  );
});
